import { createSlice, PayloadAction, createEntityAdapter } from '@reduxjs/toolkit';
import { StockData } from '../../types';

// Entity adapter for normalized stock storage
const stocksAdapter = createEntityAdapter<StockData>({
  selectId: (stock) => stock.ticker,
  sortComparer: false // We'll handle sorting in selectors
});

interface StocksState {
  loading: boolean;
  stocks: StockData[];
  sortConfig: {
    column: string | null;
    direction: 'asc' | 'desc';
  };
  lastClickedTicker: string | null;
}

// Combine entity state with custom state
type StocksEntityState = StocksState & ReturnType<typeof stocksAdapter.getInitialState>;

const initialState: StocksEntityState = stocksAdapter.getInitialState({
  loading: false,
  stocks: [],
  sortConfig: {
    column: null,
    direction: 'asc',
  },
  lastClickedTicker: null,
});

const stocksSlice = createSlice({
  name: 'stocks',
  initialState,
  reducers: {
    setStocks: (state, action: PayloadAction<StockData[]>) => {
      const payload = Array.isArray(action.payload) ? action.payload : [];
      const normalizedStocks = payload.map(stock => ({
        ...stock,
        current_price: typeof stock.current_price === 'number' ? stock.current_price : parseFloat(stock.current_price) || 0,
        market_cap: typeof stock.market_cap === 'number' ? stock.market_cap : parseFloat(stock.market_cap) || 0,
        ma_150: typeof stock.ma_150 === 'number' ? stock.ma_150 : parseFloat(stock.ma_150) || 0,
        ma_200: typeof stock.ma_200 === 'number' ? stock.ma_200 : parseFloat(stock.ma_200) || 0,
        atr_percentage: typeof stock.atr_percentage === 'number' ? stock.atr_percentage : parseFloat(stock.atr_percentage) || 0,
        target_percent: typeof stock.target_percent === 'number' ? stock.target_percent : parseFloat(stock.target_percent) || 0,
        blended_rr: stock.blended_rr == null ? null : Number(stock.blended_rr),
      }));

      // Use entity adapter to set all stocks (replaces existing)
      stocksAdapter.setAll(state, normalizedStocks);
      state.stocks = normalizedStocks;
      state.loading = false;
      state.lastClickedTicker = null;
    },
    addStock: (state, action: PayloadAction<StockData>) => {
      // Normalize single stock and add
      const normalizedStock = {
        ...action.payload,
        current_price: typeof action.payload.current_price === 'number' ? action.payload.current_price : parseFloat(action.payload.current_price) || 0,
        market_cap: typeof action.payload.market_cap === 'number' ? action.payload.market_cap : parseFloat(action.payload.market_cap) || 0,
        ma_150: typeof action.payload.ma_150 === 'number' ? action.payload.ma_150 : parseFloat(action.payload.ma_150) || 0,
        ma_200: typeof action.payload.ma_200 === 'number' ? action.payload.ma_200 : parseFloat(action.payload.ma_200) || 0,
        atr_percentage: typeof action.payload.atr_percentage === 'number' ? action.payload.atr_percentage : parseFloat(action.payload.atr_percentage) || 0,
        target_percent: typeof action.payload.target_percent === 'number' ? action.payload.target_percent : parseFloat(action.payload.target_percent) || 0,
      };
      stocksAdapter.addOne(state, normalizedStock);
      state.stocks.push(normalizedStock);
    },
    updateStock: (state, action: PayloadAction<{ ticker: string; changes: Partial<StockData> }>) => {
      stocksAdapter.updateOne(state, {
        id: action.payload.ticker,
        changes: action.payload.changes
      });
      state.stocks = state.stocks.map((stock) =>
        stock.ticker === action.payload.ticker
          ? { ...stock, ...action.payload.changes }
          : stock
      );
    },
    removeStock: (state, action: PayloadAction<string>) => {
      stocksAdapter.removeOne(state, action.payload);
      state.stocks = state.stocks.filter((stock) => stock.ticker !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSortConfig: (state, action: PayloadAction<{ column: string; direction: 'asc' | 'desc' }>) => {
      state.sortConfig = action.payload;
    },
    setLastClickedTicker: (state, action: PayloadAction<string | null>) => {
      state.lastClickedTicker = action.payload;
    },
    // Backward-compatible action retained for legacy tests.
    sortStocks: (state, action: PayloadAction<{ column: string; direction: 'asc' | 'desc' }>) => {
      const { column, direction } = action.payload;
      state.sortConfig = { column, direction };

      const sorted = [...state.stocks].sort((a: any, b: any) => {
        const av = a?.[column];
        const bv = b?.[column];
        if (av == null && bv == null) return 0;
        if (av == null) return direction === 'asc' ? -1 : 1;
        if (bv == null) return direction === 'asc' ? 1 : -1;
        if (typeof av === 'number' && typeof bv === 'number') {
          return direction === 'asc' ? av - bv : bv - av;
        }
        return direction === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });

      state.stocks = sorted;
      stocksAdapter.setAll(state, sorted);
    },
  },
});

export const { setStocks, addStock, updateStock, removeStock, setLoading, setSortConfig, setLastClickedTicker, sortStocks } = stocksSlice.actions;

// Export entity adapter selectors
export const {
  selectAll: selectAllStocks,
  selectById: selectStockById,
  selectIds: selectStockIds,
  selectEntities: selectStockEntities,
  selectTotal: selectStockCount,
} = stocksAdapter.getSelectors();

export default stocksSlice.reducer; 